/**
 * Custom size presets — user-added common non-standard sheet sizes
 * (e.g. 8.5' × 4', 9' × 3.5') persisted in app_settings.
 *
 * They render alongside the built-in SIZE_OPTIONS on Plywood /
 * Blockboard / MDF / WPC-PVC product cards, so staff logging a
 * frequently-requested custom size can do it in one click instead
 * of opening the Custom Size dialog (saving 6 clicks per line).
 *
 * Storage shape: `["8.5x4", "9x3.5"]` — strings in feet. Frontend
 * derives sqft via convertSizeToSqFt() so the same rate-expansion
 * logic (per-sqft × sheet area) applies uniformly and there's no
 * risk of the Milled-Fluted-style silent undercharge.
 */
import { useEffect, useState, useMemo } from 'react';
import { api } from '../../lib/store';
import { normalizeSizePresets } from './normalizeSizePresets';

// Re-export the pure function so the ProductsPage-side import stays
// on a single path (`from './productsPage/customSizePresets'`). Tests
// import directly from the pure module to avoid axios ESM issues.
export { normalizeSizePresets };


/**
 * React hook: fetch app_settings once, expose custom size presets
 * as SIZE_OPTIONS-shaped rows. Silent-fails on network error so a
 * settings hiccup never blocks the Products page from loading.
 */
export function useCustomSizePresets() {
  const [rawPresets, setRawPresets] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.getSettings().then(r => {
      if (cancelled) return;
      const list = (r.data && Array.isArray(r.data.custom_size_presets))
        ? r.data.custom_size_presets
        : [];
      setRawPresets(list);
    }).catch(() => { /* non-fatal — presets are optional */ });
    return () => { cancelled = true; };
  }, []);

  // Memoize the normalized list so downstream useMemo dependencies stay
  // stable across re-renders when the raw list hasn't changed.
  return useMemo(() => rawPresets, [rawPresets]);
}
